import { getFormIsInvalidAtom } from 'atoms/form-atoms';
import {
  Button,
  Dropdown,
  Form,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import { TextAreaField } from 'components/form-fields/text-area-field';
import { TextField } from 'components/form-fields/text-field';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useMemo } from 'react';
import {
  createCrmAccountFormAtom,
  resetCreateCrmAccountFormAtom,
} from './atoms';
import style from './create-crm-account.module.scss';

interface CreateCrmAccountProps {
  isOpen: boolean;
  onClose: () => void;
  initialAccountName?: string;
  accountTypeOptions: Array<{ value: string; label: string }>;
  accountSourceOptions: Array<{ value: string; label: string }>;
  onCreate: (payload: {
    accountName: string;
    type: string;
    phoneNumber: string;
    accountSource: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  }) => void;
  createAccountError?: string | null;
  isCreatingAccount?: boolean;
}

export const CreateCrmAccount: React.FC<CreateCrmAccountProps> = ({
  isOpen,
  onClose,
  initialAccountName = '',
  accountTypeOptions,
  accountSourceOptions,
  onCreate,
  createAccountError,
  isCreatingAccount = false,
}) => {
  const form = useAtomValue(createCrmAccountFormAtom);

  // Create validation atom to check if form is invalid
  const formIsInvalidAtom = useMemo(
    () => getFormIsInvalidAtom(atom(form)),
    [form]
  );
  const isFormInvalid = useAtomValue(formIsInvalidAtom);

  const [accountName, setAccountName] = useAtom(form.accountName);
  const [type, setType] = useAtom(form.type);
  const [accountSource, setAccountSource] = useAtom(form.accountSource);
  const phoneNumber = useAtomValue(form.phoneNumber);
  const address = useAtomValue(form.address);
  const city = useAtomValue(form.city);
  const state = useAtomValue(form.state);
  const zipCode = useAtomValue(form.zipCode);
  const resetForm = useSetAtom(resetCreateCrmAccountFormAtom);

  // Reset form and pre-fill account name when panel opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      // Then pre-fill account name if provided
      if (initialAccountName) {
        setAccountName(initialAccountName);
      }
    }
  }, [isOpen, initialAccountName, resetForm, setAccountName]);

  if (!isOpen) return null;

  return (
    <div className={style.panel}>
      <div className={style.panelHeader}>
        <h3 className={style.panelTitle}>Create CRM Account</h3>
        <div className={style.panelActions}>
          <Button kind="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            kind="primary"
            size="sm"
            data-testid="create-crm-account-submit-button"
            disabled={isFormInvalid}
            onClick={() =>
              onCreate({
                accountName: accountName.value,
                type: type.value,
                phoneNumber: phoneNumber.value,
                accountSource: accountSource.value,
                address: address.value,
                city: city.value,
                state: state.value,
                zipCode: zipCode.value,
              })
            }
          >
            Create
          </Button>
        </div>
      </div>

      <div className={style.panelContent}>
        {isCreatingAccount && (
          <div className={style.loadingContainer}>
            <InlineLoading
              role="status"
              aria-label="Creating account..."
              description="Creating account..."
            />
          </div>
        )}
        {createAccountError && (
          <div className={style.errorNotificationContainer}>
            <InlineNotification kind="error" title={createAccountError} />
          </div>
        )}
        <h4 className={style.panelSectionTitle}>Details</h4>
        <Form>
          <div className={style.formRowFullWidth}>
            <TextField
              id="crm-account-name"
              labelText="Account Name*"
              fieldAtom={form.accountName}
            />
          </div>
          <div className={style.formRowTwoColumns}>
            <div className={style.dropdownWrapper}>
              <Dropdown
                id="crm-type"
                label="Choose an option"
                titleText="Type*"
                items={accountTypeOptions}
                selectedItem={
                  accountTypeOptions.find((opt) => opt.value === type.value) ||
                  null
                }
                itemToString={(item) => (item ? item.label : '')}
                onChange={(event) => {
                  if (event.selectedItem) {
                    setType(event.selectedItem.value);
                  }
                }}
                invalid={!type.isValid && type.isDirty}
                invalidText={!type.isValid && type.isDirty ? type.error : ''}
              />
            </div>
            <TextField
              id="crm-phone-number"
              labelText="Phone Number"
              fieldAtom={form.phoneNumber}
            />
          </div>
          <div className={style.formRowFullWidth}>
            <div className={style.dropdownWrapper}>
              <Dropdown
                id="crm-account-source"
                label="Choose an option"
                titleText="Account Source*"
                items={accountSourceOptions}
                selectedItem={
                  accountSourceOptions.find(
                    (opt) => opt.value === accountSource.value
                  ) || null
                }
                itemToString={(item) => (item ? item.label : '')}
                onChange={(event) => {
                  if (event.selectedItem) {
                    setAccountSource(event.selectedItem.value);
                  }
                }}
                invalid={!accountSource.isValid && accountSource.isDirty}
                invalidText={
                  !accountSource.isValid && accountSource.isDirty
                    ? accountSource.error
                    : ''
                }
              />
            </div>
          </div>

          <div className={style.formRowFullWidth}>
            <TextAreaField
              id="crm-address"
              labelText="Address"
              fieldAtom={form.address}
              rows={3}
            />
          </div>

          <div className={style.formRowThreeColumns}>
            <TextField id="crm-city" labelText="City" fieldAtom={form.city} />
            <TextField
              id="crm-state"
              labelText="State"
              fieldAtom={form.state}
            />
            <TextField
              id="crm-zip"
              labelText="Zip Code"
              fieldAtom={form.zipCode}
            />
          </div>
        </Form>
      </div>
    </div>
  );
};
